import { StrictMode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import type { components } from "./generated/api";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import "./styles.css";
import { Processes, ProcessWorkspace } from "./processes";
import { AccountWorkspace } from "./workspace";
import { AcceptInvitation, AccountSettings, AccountTeamSecurity, MarketplaceSettings, SessionGate, SignOut, UsersSettings } from "./session";

type Account = components["schemas"]["AccountView"];
type Session = components["schemas"]["SessionActor"];
type SortKey = "name" | "lifecycle" | "salesStage" | "timezone";
type SortDirection = "ascending" | "descending";

const modules = [
  ["Accounts", "Customer lifecycle, ownership and access"],
  ["Development", "Delivery projects and readiness gates"],
  ["Maintenance", "Service work, incidents and reviews"],
  ["Onboarding", "Customer readiness and next actions"],
  ["Renewal", "Value reviews and renewal progress"],
  ["Termination", "Offboarding and verified access removal"]
] as const;

const lifecycleOptions = ["all", "prospect", "evaluating", "active", "lost", "suspended", "former"] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readOptionalString(value: unknown): value is string | null | undefined {
  return value === null || value === undefined || typeof value === "string";
}

function isAccount(value: unknown): value is Account {
  if (!isRecord(value)) return false;
  return typeof value.id === "string" && typeof value.name === "string" &&
    typeof value.lifecycle === "string" && typeof value.salesStage === "string" &&
    typeof value.timezone === "string" && typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string" && readOptionalString(value.ownerId) &&
    readOptionalString(value.implementationLeadId);
}

function parseAccounts(value: unknown): Account[] {
  if (!Array.isArray(value) || !value.every(isAccount)) throw new Error("The accounts response is invalid");
  return value;
}

async function loadAccounts(signal: AbortSignal, search: string, lifecycle: string): Promise<Account[]> {
  const response = await fetch(`/v1/accounts?${new URLSearchParams({ search, lifecycle }).toString()}`, { signal, headers: { Accept: "application/json" } });
  if (response.status === 401) {
    window.location.assign("/login");
    return [];
  }
  if (!response.ok) throw new Error(`Accounts could not be loaded (${String(response.status)})`);
  const payload: unknown = JSON.parse(await response.text());
  return parseAccounts(payload);
}

function formatLabel(value: string): string {
  return value.split("_").map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`).join(" ");
}

const navigation = [
  ["/", "Home", "home"],
  ["/accounts", "Accounts", "accounts"],
  ["/onboarding", "Onboarding", "onboarding"],
  ["/development", "Development", "development"],
  ["/maintenance", "Maintenance", "maintenance"],
  ["/renewal", "Renewal", "renewal"],
  ["/termination", "Termination", "termination"]
] as const;

type NavigationIcon = (typeof navigation)[number][2] | "settings" | "user" | "back";

function NavIcon({ name }: { name: NavigationIcon }) {
  const paths: Record<NavigationIcon, string> = {
    home: "M3 10.5 12 3l9 7.5V21h-6v-6H9v6H3V10.5Z",
    accounts: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    onboarding: "M20 6 9 17l-5-5M12 3h9v9",
    development: "m8 9-5 3 5 3M16 9l5 3-5 3M14 5l-4 14",
    maintenance: "M14.7 6.3a4 4 0 0 0-5-5L7.4 3.6l3 3-4 4-3-3-2.3 2.3a4 4 0 0 0 5 5L15 23.8l3.8-3.8-8.6-8.7a4 4 0 0 0 4.5-5Z",
    renewal: "M20 11a8.1 8.1 0 1 0 .2 4M20 4v7h-7",
    termination: "M5 5l14 14M19 5 5 19",
    settings: "M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21h-4v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3v-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.5V3h4v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.1v4h-.1a1.7 1.7 0 0 0-1.5 1Z",
    user: "M20 21a8 8 0 0 0-16 0M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z",
    back: "m15 18-6-6 6-6"
  };
  return <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true"><path d={paths[name]} /></svg>;
}

function Sidebar({ session, signOut }: { session: Session; signOut: React.ReactNode }) {
  const [expanded, setExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = window.location.pathname;
  const currentAccount = /^\/accounts\/([0-9a-f-]{36})(?:\/|$)/i.exec(pathname);
  const currentAccountId = currentAccount?.[1];
  const accountSettings = /^\/accounts\/([0-9a-f-]{36})\/settings(?:\/([^/]+))?$/i.exec(pathname);
  const settingsMode = pathname === "/settings" || pathname.startsWith("/settings/") || accountSettings !== null;
  const accountSettingsBase = currentAccountId === undefined ? null : `/accounts/${currentAccountId}/settings`;
  const savedReturn = sessionStorage.getItem("facio.settings.returnTo");
  const backToWorkspace = savedReturn?.startsWith("/") && !savedReturn.startsWith("//") ? savedReturn : currentAccountId === undefined ? "/accounts" : `/accounts/${currentAccountId}`;
  const initials = session.displayName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join("") || "FO";
  function isActive(href: string): boolean {
    return href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
  }
  return <>
    <button className="mobile-menu-toggle" type="button" aria-label={mobileOpen ? "Close navigation" : "Open navigation"} aria-controls="primary-sidebar" aria-expanded={mobileOpen} onClick={() => { setMobileOpen(current => !current); }}><span /><span /><span /></button>
    {mobileOpen ? <button className="sidebar-scrim" type="button" aria-label="Dismiss navigation" onClick={() => { setMobileOpen(false); }} /> : null}
    <aside id="primary-sidebar" className={`sidebar ${expanded ? "expanded" : "collapsed"} ${mobileOpen ? "mobile-open" : ""}`}>
      <div className="sidebar-brand">
        <a href="/" aria-label="FacioOS home"><img className="brand-lockup" src="/facio-logo.png" alt="Facio" /><img className="brand-mark" src="/facio-mark.svg" alt="" /><span className="product-name">FacioOS</span></a>
        <button className="sidebar-toggle" type="button" aria-label={expanded ? "Collapse navigation" : "Expand navigation"} aria-expanded={expanded} onClick={() => { setExpanded(current => !current); }}>‹</button>
      </div>
      <nav className="sidebar-nav" aria-label={settingsMode ? "Settings navigation" : "Primary navigation"}>
        {settingsMode ? <>
          <a href={backToWorkspace}><NavIcon name="back" /><span>Back to workspace</span></a>
          {accountSettingsBase !== null ? <>
            <a className={pathname.endsWith("/team") ? "active" : ""} aria-current={pathname.endsWith("/team") ? "page" : undefined} href={`${accountSettingsBase}/team`}><NavIcon name="accounts" /><span>Team and security</span></a>
            <a className={pathname.endsWith("/integrations") ? "active" : ""} aria-current={pathname.endsWith("/integrations") ? "page" : undefined} href={`${accountSettingsBase}/integrations`}><NavIcon name="renewal" /><span>Integrations</span></a>
            <a className={pathname.endsWith("/developer") ? "active" : ""} href={`${accountSettingsBase}/developer`}><NavIcon name="development" /><span>Developer access</span></a>
            <a className={pathname.endsWith("/email-review") ? "active" : ""} href={`${accountSettingsBase}/email-review`}><NavIcon name="maintenance" /><span>Email review</span></a>
            <a className={pathname.endsWith("/general") ? "active" : ""} href={`${accountSettingsBase}/general`}><NavIcon name="settings" /><span>General</span></a>
          </> : null}
          {accountSettingsBase === null && session.platformRole === "system_admin" ? <a className={isActive("/settings/users") || pathname === "/settings" ? "active" : ""} aria-current={isActive("/settings/users") || pathname === "/settings" ? "page" : undefined} href="/settings/users"><NavIcon name="accounts" /><span>Users</span></a> : null}
          {accountSettingsBase === null && session.platformRole === "system_admin" ? <a className={isActive("/settings/marketplace") ? "active" : ""} aria-current={isActive("/settings/marketplace") ? "page" : undefined} href="/settings/marketplace"><NavIcon name="renewal" /><span>Integrations</span></a> : null}
          <a className={isActive("/settings/account") || (pathname === "/settings" && session.platformRole !== "system_admin") ? "active" : ""} aria-current={isActive("/settings/account") ? "page" : undefined} href="/settings/account"><NavIcon name="user" /><span>My account</span></a>
        </> : navigation.map(([href, label, icon]) => <a className={isActive(href) ? "active" : ""} aria-current={isActive(href) ? "page" : undefined} href={href} key={href}><NavIcon name={icon} /><span>{label}</span></a>)}
      </nav>
      <div className="sidebar-account">
        <details className="profile-menu"><summary className={settingsMode ? "account-link active" : "account-link"}><span className="account-avatar">{initials}</span><span className="account-copy"><strong>{session.displayName}</strong><small>{session.email}</small></span><NavIcon name="settings" /></summary>
          <div className="profile-menu-popover"><a href={accountSettingsBase !== null ? `${accountSettingsBase}/team` : session.platformRole === "system_admin" ? "/settings/users" : "/settings/account"} onClick={() => { if (!settingsMode) sessionStorage.setItem("facio.settings.returnTo", `${location.pathname}${location.search}${location.hash}`); }}><NavIcon name="settings" />Settings</a>{signOut}</div>
        </details>
      </div>
    </aside>
  </>;
}

function Home() {
  return <>
    <section className="hero"><p>Customer operations</p><h1>Every customer, delivery and service decision in one place.</h1><span>Manage accounts and their next steps.</span></section>
    <section className="grid" aria-label="FacioOS modules">
      {modules.map(([name, detail], index) => <article key={name}><span>{String(index + 1).padStart(2, "0")}</span><h2>{name}</h2><p>{detail}</p><a href={`/${name.toLowerCase()}`}>Open <b aria-hidden="true">→</b></a></article>)}
    </section>
  </>;
}

function Accounts({ csrfToken, canCreate }: { csrfToken: string; canCreate: boolean }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [lifecycle, setLifecycle] = useState<(typeof lifecycleOptions)[number]>("prospect");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ascending");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(undefined);
    loadAccounts(controller.signal, search, lifecycle)
      .then(setAccounts)
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setError(reason instanceof Error ? reason.message : "Accounts could not be loaded");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { controller.abort(); };
  }, [search, lifecycle]);

  const visibleAccounts = useMemo(() => {
    return accounts
      .filter(account => lifecycle === "all" || account.lifecycle === lifecycle)
      .slice()
      .sort((left, right) => {
        const comparison = left[sortKey].localeCompare(right[sortKey], undefined, { sensitivity: "base" });
        return sortDirection === "ascending" ? comparison : -comparison;
      });
  }, [accounts, lifecycle, sortDirection, sortKey]);

  function changeSort(nextKey: SortKey) {
    if (nextKey === sortKey) setSortDirection(current => current === "ascending" ? "descending" : "ascending");
    else {
      setSortKey(nextKey);
      setSortDirection("ascending");
    }
  }

  function sortIndicator(key: SortKey): string {
    if (key !== sortKey) return "";
    return sortDirection === "ascending" ? " ↑" : " ↓";
  }

  return <section className="accounts-page">
    <div className="page-heading"><div><p>Customer directory</p><h1>Accounts</h1></div><span>{visibleAccounts.length} shown</span></div>
    {canCreate ? <CreateAccount csrfToken={csrfToken} /> : null}
    <div className="toolbar">
      <label><span>Search accounts</span><input type="search" value={search} onChange={event => { setSearch(event.target.value); }} placeholder="Account, contact name, email or domain" /></label>
      <div className="filters" aria-label="Lifecycle filter">
        {lifecycleOptions.map(option => <button type="button" className={lifecycle === option ? "active" : ""} onClick={() => { setLifecycle(option); }} key={option}>{formatLabel(option)}</button>)}
      </div>
    </div>
    {loading ? <p className="state">Loading accounts…</p> : null}
    {error === undefined ? null : <p className="state error" role="alert">{error}</p>}
    {!loading && error === undefined ? <div className="table-wrap"><table>
      <thead><tr>
        <th><button type="button" onClick={() => { changeSort("name"); }}>Account{sortIndicator("name")}</button></th>
        <th><button type="button" onClick={() => { changeSort("lifecycle"); }}>Lifecycle{sortIndicator("lifecycle")}</button></th>
        <th><button type="button" onClick={() => { changeSort("salesStage"); }}>Stage{sortIndicator("salesStage")}</button></th>
        <th><button type="button" onClick={() => { changeSort("timezone"); }}>Timezone{sortIndicator("timezone")}</button></th>
      </tr></thead>
      <tbody>{visibleAccounts.map(account => <tr key={account.id}><td><a href={`/accounts/${account.id}`}>{account.name}</a></td><td><span className={`status ${account.lifecycle}`}>{formatLabel(account.lifecycle)}</span></td><td>{formatLabel(account.salesStage)}</td><td>{account.timezone}</td></tr>)}</tbody>
    </table>{visibleAccounts.length === 0 ? <p className="empty">No accounts match these filters.</p> : null}</div> : null}
  </section>;
}

function CreateAccount({ csrfToken }: { csrfToken: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  async function submit(event: React.SubmitEvent<HTMLFormElement>) {
    event.preventDefault(); const fields = new FormData(event.currentTarget); setBusy(true); setError(undefined);
    try {
      const response = await fetch("/v1/accounts", { method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken }, body: JSON.stringify({ name: fields.get("name"), timezone: fields.get("timezone") }) });
      if (response.status === 401) { window.location.assign("/login"); return; }
      if (!response.ok) throw new Error("The account could not be created. Check your permissions and required fields.");
      const value: unknown = await response.json();
      if (!isAccount(value)) throw new Error("The created account response is invalid.");
      window.location.assign(`/accounts/${value.id}`);
    } catch (reason: unknown) { setError(reason instanceof Error ? reason.message : "Account creation failed."); setBusy(false); }
  }
  return <details className="create-account"><summary>Create account</summary>{error === undefined ? null : <p role="alert">{error}</p>}<form className="workspace-form" onSubmit={event => { void submit(event); }}><fieldset disabled={busy}><legend>New customer account</legend><label>Customer name<input required name="name" maxLength={240} /></label><label>Customer timezone<select aria-label="Customer timezone" name="timezone" defaultValue="Asia/Jerusalem">{Intl.supportedValuesOf("timeZone").map(zone => <option key={zone} value={zone}>{zone}</option>)}</select></label><button type="submit">Create customer</button></fieldset></form></details>;
}

function App() {
  const processId = /^\/processes\/([0-9a-f-]{36})$/i.exec(window.location.pathname)?.[1];
  const processPhase = ["onboarding", "development", "maintenance", "renewal", "termination"].find(phase => window.location.pathname === `/${phase}`);
  const accountId = /^\/accounts\/([0-9a-f-]{36})$/i.exec(window.location.pathname)?.[1];
  const accountSettingsMatch = /^\/accounts\/([0-9a-f-]{36})\/settings(?:\/([^/]+))?$/i.exec(window.location.pathname);
  const accountSettingsId = accountSettingsMatch?.[1];
  const invitationToken = /^\/invitations\/([A-Za-z0-9_-]{43})$/.exec(window.location.pathname)?.[1];
  return <SessionGate>{session => <div className="app-shell"><Sidebar session={session} signOut={<SignOut session={session} />} /><main className="app-content">{
    invitationToken !== undefined ? <AcceptInvitation token={invitationToken} session={session} /> :
    accountSettingsId !== undefined ? <AccountTeamSecurity accountId={accountSettingsId} section={accountSettingsMatch?.[2] ?? "team"} session={session} /> :
    processId !== undefined ? <ProcessWorkspace processId={processId} csrfToken={session.csrfToken} /> :
    processPhase !== undefined ? <Processes phase={processPhase} csrfToken={session.csrfToken} /> :
    accountId !== undefined ? <AccountWorkspace accountId={accountId} csrfToken={session.csrfToken} /> :
    window.location.pathname === "/accounts" ? <Accounts csrfToken={session.csrfToken} canCreate={session.platformRole === "system_admin"} /> :
    (window.location.pathname === "/settings" || window.location.pathname === "/settings/users") && session.platformRole === "system_admin" ? <UsersSettings session={session} /> :
    window.location.pathname === "/settings/marketplace" && session.platformRole === "system_admin" ? <MarketplaceSettings session={session} /> :
    window.location.pathname === "/settings" || window.location.pathname === "/settings/account" ? <AccountSettings session={session} /> :
    window.location.pathname === "/" || window.location.pathname === "/login" ? <Home /> :
    <section className="accounts-page"><h1>Page unavailable</h1><p>This page is not available in this release.</p><a href="/accounts">Return to accounts</a></section>
  }</main></div>}</SessionGate>;
}

const root = document.getElementById("root");
if (root === null) throw new Error("FacioOS root element is missing");
createRoot(root).render(<StrictMode><App /></StrictMode>);
